<template>
    <div class="flex max-w-4xl flex-col gap-8">
        <!-- Período + PDF: renderizados na toolbar única do UserAudit.
             Sem o alvo, rendem inline no topo. -->
        <Teleport
            v-if="mounted"
            to="#audit-toolbar"
            :disabled="!toolbarTargetExists"
        >
            <div class="flex min-w-0 flex-wrap items-center gap-2">
                <div
                    role="group"
                    aria-label="Selecionar período"
                    class="inline-flex items-center gap-0.5 rounded-lg bg-gray-100 p-0.5 dark:bg-gray-800"
                >
                    <button
                        v-for="option in presets"
                        :key="option.key"
                        type="button"
                        :aria-pressed="preset === option.key"
                        @click="applyPreset(option.key)"
                        :class="[
                            'rounded-md px-2.5 py-1 text-xs font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                            preset === option.key
                                ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white'
                                : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white',
                        ]"
                    >
                        {{ option.label }}
                    </button>
                </div>

                <div
                    v-if="preset === 'custom'"
                    class="flex items-center gap-1.5 text-xs"
                >
                    <input
                        type="date"
                        v-model="startDate"
                        :max="endDate"
                        @change="onCustomDate"
                        aria-label="Data inicial"
                        class="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                    />
                    <span class="text-gray-400">até</span>
                    <input
                        type="date"
                        v-model="endDate"
                        :min="startDate"
                        :max="today"
                        @change="onCustomDate"
                        aria-label="Data final"
                        class="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                    />
                </div>

                <button
                    type="button"
                    :disabled="pdf.generating || !report"
                    @click="generatePdf"
                    class="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:border-gray-300 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:text-white"
                >
                    <ArrowPathIcon
                        v-if="pdf.generating"
                        class="h-4 w-4 animate-spin motion-reduce:animate-none"
                    />
                    <ArrowDownTrayIcon v-else class="h-4 w-4" />
                    {{ pdf.generating ? "Gerando PDF…" : "Gerar PDF" }}
                </button>
            </div>
        </Teleport>

        <p
            v-if="pdf.error"
            role="alert"
            class="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:bg-rose-500/10 dark:text-rose-300"
        >
            {{ pdf.error }}
        </p>

        <p v-if="error" role="alert" class="text-sm text-rose-500">
            {{ error }}
        </p>

        <div v-else-if="loading && !report" class="flex flex-col gap-4">
            <div
                class="h-12 w-56 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800"
            ></div>
            <div
                class="h-4 w-full animate-pulse rounded-full bg-gray-100 dark:bg-gray-800"
            ></div>
        </div>

        <template v-else-if="report">
            <div
                :class="[
                    'flex flex-col gap-8 transition-opacity',
                    loading ? 'opacity-50' : '',
                ]"
                :aria-busy="loading"
            >
                <p
                    v-if="!hasAttempts"
                    class="rounded-xl border border-dashed border-gray-300 px-5 py-10 text-center text-sm text-gray-500 dark:border-gray-600 dark:text-gray-400"
                >
                    Nenhuma tentativa de cartão entre
                    {{ formatDate(report.period.start) }} e
                    {{ formatDate(report.period.end) }}. Escolha um período
                    maior.
                </p>

                <template v-else>
                    <section class="flex flex-col gap-5">
                        <div
                            class="flex flex-col gap-5 sm:flex-row sm:items-end sm:gap-12"
                        >
                            <div>
                                <h2
                                    class="text-sm font-medium text-gray-500 dark:text-gray-400"
                                >
                                    Taxa de aprovação
                                </h2>
                                <p
                                    class="text-5xl font-bold tabular-nums tracking-tight text-violet-700 dark:text-violet-300"
                                >
                                    {{ formatRate(report.approval.rate) }}
                                </p>
                                <p
                                    class="mt-1 text-sm text-gray-600 dark:text-gray-300"
                                >
                                    A Ticto aprovou
                                    {{ formatInt(report.approval.approved) }}
                                    de
                                    {{ formatInt(report.approval.attempts) }}
                                    tentativas que dava para aprovar.
                                </p>
                            </div>

                            <div class="sm:pb-0.5">
                                <h2
                                    class="text-sm font-medium text-gray-500 dark:text-gray-400"
                                >
                                    Aprovação bruta
                                </h2>
                                <p
                                    class="text-2xl font-semibold tabular-nums text-gray-800 dark:text-gray-100"
                                >
                                    {{ formatRate(report.gross.rate) }}
                                </p>
                                <p
                                    class="mt-1 text-sm text-gray-600 dark:text-gray-300"
                                >
                                    {{ formatInt(report.gross.approved) }}
                                    aprovadas e
                                    {{ formatInt(report.gross.refused) }}
                                    recusas, contando tudo.
                                </p>
                            </div>
                        </div>

                        <p
                            v-if="report.approval.small_sample"
                            class="text-sm font-medium text-amber-700 dark:text-amber-400"
                        >
                            Amostra pequena: menos de 30 tentativas no período.
                            Use um período maior antes de concluir algo.
                        </p>

                        <figure class="flex flex-col gap-2">
                            <div
                                class="flex h-4 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800"
                                role="img"
                                :aria-label="compositionLabel"
                            >
                                <div
                                    v-for="segment in segments"
                                    :key="segment.key"
                                    :class="segment.bar"
                                    :style="{ width: segment.width }"
                                ></div>
                            </div>

                            <div class="flex text-xs">
                                <div
                                    :style="{ width: insideWidth }"
                                    class="min-w-0 border-t-2 border-violet-600 pt-1 text-violet-700 dark:border-violet-400 dark:text-violet-300"
                                >
                                    <span class="whitespace-nowrap"
                                        >Entra na taxa de aprovação</span
                                    >
                                </div>
                                <div
                                    v-if="report.audit.never_approves"
                                    class="min-w-0 flex-1 truncate pt-1 pl-2 text-right text-gray-400 dark:text-gray-500"
                                >
                                    Fica de fora
                                </div>
                            </div>

                            <figcaption
                                class="mt-1 flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-600 dark:text-gray-300"
                            >
                                <span
                                    v-for="segment in segments"
                                    :key="segment.key"
                                    class="inline-flex items-center gap-1.5"
                                >
                                    <span
                                        :class="[
                                            'h-2.5 w-2.5 rounded-sm',
                                            segment.bar,
                                        ]"
                                    ></span>
                                    {{ segment.label }}
                                    <b
                                        class="font-semibold tabular-nums text-gray-900 dark:text-white"
                                        >{{ formatInt(segment.count) }}</b
                                    >
                                </span>
                            </figcaption>
                        </figure>
                    </section>

                    <div class="grid grid-cols-1 gap-8 md:grid-cols-2">
                        <section
                            v-for="group in reasonGroups"
                            :key="group.key"
                            class="flex flex-col gap-3"
                        >
                            <div>
                                <h3
                                    class="text-sm font-semibold text-gray-900 dark:text-white"
                                >
                                    {{ group.title }}
                                </h3>
                                <p
                                    class="mt-0.5 text-xs text-gray-500 dark:text-gray-400"
                                >
                                    {{ group.hint }}
                                </p>
                            </div>

                            <ul
                                v-if="group.rows.length"
                                class="flex flex-col gap-2.5"
                            >
                                <li
                                    v-for="reason in group.rows"
                                    :key="reason.label"
                                    class="flex flex-col gap-1"
                                >
                                    <div
                                        class="flex items-baseline justify-between gap-3 text-sm"
                                    >
                                        <span
                                            class="min-w-0 text-gray-700 dark:text-gray-200"
                                            >{{ reason.label }}</span
                                        >
                                        <span
                                            class="shrink-0 tabular-nums text-gray-900 dark:text-white"
                                        >
                                            {{ formatInt(reason.count) }}
                                            <span
                                                class="ml-1 text-xs text-gray-400"
                                                >{{
                                                    formatRate(reason.share)
                                                }}</span
                                            >
                                        </span>
                                    </div>
                                    <div
                                        class="h-1 w-full rounded-full bg-gray-100 dark:bg-gray-800"
                                    >
                                        <div
                                            :class="[
                                                'h-1 rounded-full',
                                                group.bar,
                                            ]"
                                            :style="{
                                                width: `${reason.share ?? 0}%`,
                                            }"
                                        ></div>
                                    </div>
                                </li>
                            </ul>
                            <p
                                v-else
                                class="text-sm text-gray-400 dark:text-gray-500"
                            >
                                Nenhuma recusa deste grupo no período.
                            </p>
                        </section>
                    </div>
                </template>

                <footer
                    class="flex flex-col gap-3 border-t border-gray-200 pt-4 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400"
                >
                    <p>
                        Cartão de crédito, 1ª compra, de
                        {{ formatDate(report.period.start) }} a
                        {{ formatDate(report.period.end) }}.
                        {{ formatInt(report.audit.card_testing) }} testes de
                        cartão barrados pelo antifraude e
                        {{ formatInt(report.audit.technical_errors) }} erros
                        técnicos ficaram fora das duas taxas.
                        {{ formatInt(report.audit.collapsed_repetitions) }}
                        repetições em até 2 segundos e
                        {{ formatInt(report.audit.collapsed_second_card) }}
                        cobranças de segundo cartão contaram uma vez.
                    </p>

                    <details class="group">
                        <summary
                            class="inline-flex cursor-pointer items-center gap-1 rounded font-medium text-gray-700 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:text-gray-200 dark:hover:text-white"
                        >
                            <ChevronRightIcon
                                class="h-3.5 w-3.5 transition-transform group-open:rotate-90 motion-reduce:transition-none"
                            />
                            Como calculamos
                        </summary>
                        <div
                            class="mt-3 flex max-w-prose flex-col gap-2 text-sm leading-relaxed text-gray-600 dark:text-gray-300"
                        >
                            <p>
                                <b class="text-gray-900 dark:text-white"
                                    >Taxa de aprovação</b
                                >
                                é tentativas aprovadas dividido por tentativas
                                aprovadas mais recusas que podiam aprovar. Só
                                três motivos podiam aprovar: transação negada,
                                suspeita de fraude e contato com o emissor.
                            </p>
                            <p>
                                Antes da conta, as repetições da mesma cobrança
                                em até 2 segundos e o segundo cartão do mesmo
                                pedido viram uma tentativa só, com o melhor
                                resultado.
                            </p>
                            <p>
                                <b class="text-gray-900 dark:text-white"
                                    >Aprovação bruta</b
                                >
                                é aprovadas dividido por aprovadas mais todas as
                                recusas, sem juntar nada. É o número que o
                                produtor calcula sozinho.
                            </p>
                            <p>
                                Renovações de assinatura ficam fora. Mapa de
                                motivos versão {{ report.map_version }}.
                            </p>
                        </div>
                    </details>
                </footer>
            </div>
        </template>
    </div>
</template>

<script>
import {
    ArrowDownTrayIcon,
    ArrowPathIcon,
    ChevronRightIcon,
} from "@heroicons/vue/24/outline";

const PDF_POLL_INTERVAL_MS = 2000;
const PDF_POLL_TIMEOUT_MS = 180000;

const isoDate = (date) => {
    const offset = date.getTimezoneOffset() * 60000;

    return new Date(date.getTime() - offset).toISOString().slice(0, 10);
};

const daysAgo = (days) => {
    const date = new Date();
    date.setDate(date.getDate() - days);

    return isoDate(date);
};

const percentOf = (part, total) =>
    total > 0 ? `${(part / total) * 100}%` : "0%";

export default {
    components: { ArrowDownTrayIcon, ArrowPathIcon, ChevronRightIcon },
    props: ["user"],

    data() {
        return {
            presets: [
                { key: "7", label: "7 dias" },
                { key: "30", label: "30 dias" },
                { key: "90", label: "90 dias" },
                { key: "365", label: "12 meses" },
                { key: "last-month", label: "Mês anterior" },
                { key: "custom", label: "Personalizado" },
            ],
            preset: "30",
            startDate: daysAgo(29),
            endDate: isoDate(new Date()),
            today: isoDate(new Date()),
            report: null,
            loading: false,
            error: null,
            pdf: { generating: false, error: null, timer: null },
            mounted: false,
            toolbarTargetExists: false,
        };
    },

    computed: {
        userId() {
            return this.$route.params.user_id ?? this.user.id;
        },

        query() {
            return new URLSearchParams({
                start_date: this.startDate,
                end_date: this.endDate,
            }).toString();
        },

        decisions() {
            return (
                this.report.approval.attempts + this.report.audit.never_approves
            );
        },

        hasAttempts() {
            return this.decisions > 0;
        },

        segments() {
            const { approval, audit } = this.report;

            return [
                {
                    key: "approved",
                    label: "Aprovadas",
                    count: approval.approved,
                    bar: "bg-violet-600 dark:bg-violet-400",
                },
                {
                    key: "could-approve",
                    label: "Recusas que podiam aprovar",
                    count: approval.could_approve_refusals,
                    bar: "bg-amber-500 dark:bg-amber-400",
                },
                {
                    key: "never-approves",
                    label: "Recusas que nunca aprovariam",
                    count: audit.never_approves,
                    bar: "bg-gray-300 dark:bg-gray-600",
                },
            ].map((segment) => ({
                ...segment,
                width: percentOf(segment.count, this.decisions),
            }));
        },

        insideWidth() {
            return percentOf(this.report.approval.attempts, this.decisions);
        },

        compositionLabel() {
            return this.segments
                .map((segment) => `${segment.label}: ${segment.count}`)
                .join(", ");
        },

        reasonGroups() {
            return [
                {
                    key: "could-approve",
                    title: "Recusas que podiam ter aprovado",
                    hint: "Aqui a Ticto atua com roteamento, retentativa e 3DS.",
                    bar: "bg-amber-500 dark:bg-amber-400",
                    rows: this.report.could_approve_reasons,
                },
                {
                    key: "never-approves",
                    title: "Recusas que nunca aprovariam",
                    hint: "Nenhuma plataforma aprovaria estas tentativas como estavam.",
                    bar: "bg-gray-400 dark:bg-gray-500",
                    rows: this.report.never_approves_reasons,
                },
            ];
        },
    },

    mounted() {
        this.toolbarTargetExists = Boolean(
            document.getElementById("audit-toolbar"),
        );
        this.mounted = true;
        this.fetch();
    },

    beforeUnmount() {
        this.stopPdfPolling();
    },

    methods: {
        applyPreset(key) {
            this.preset = key;

            if (key === "custom") {
                return;
            }

            this.endDate = this.today;

            if (key === "last-month") {
                const now = new Date();
                this.startDate = isoDate(
                    new Date(now.getFullYear(), now.getMonth() - 1, 1),
                );
                this.endDate = isoDate(
                    new Date(now.getFullYear(), now.getMonth(), 0),
                );
            } else {
                this.startDate = daysAgo(Number(key) - 1);
            }

            this.fetch();
        },

        onCustomDate() {
            if (!this.startDate || !this.endDate) {
                return;
            }

            this.fetch();
        },

        fetch() {
            this.loading = true;
            this.error = null;

            axios
                .get(`/api/user/${this.userId}/card-approval?${this.query}`)
                .then(({ data }) => {
                    this.report = data;
                })
                .catch((error) => {
                    this.error =
                        error.response?.data?.errors?.[0] ??
                        "Não foi possível carregar a taxa de aprovação. Tente de novo.";
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        generatePdf() {
            this.stopPdfPolling();
            this.pdf.generating = true;
            this.pdf.error = null;

            axios
                .post(
                    `/api/user/${this.userId}/card-approval/pdf?${this.query}`,
                )
                .then(({ data }) => this.pollPdf(data.id, Date.now()))
                .catch((error) =>
                    this.failPdf(
                        error.response?.data?.errors?.[0] ??
                            "Não foi possível pedir o PDF. Tente de novo.",
                    ),
                );
        },

        pollPdf(exportId, startedAt) {
            if (Date.now() - startedAt > PDF_POLL_TIMEOUT_MS) {
                this.failPdf(
                    "O PDF está demorando mais que o normal. Tente de novo em alguns minutos.",
                );

                return;
            }

            this.pdf.timer = setTimeout(() => {
                axios
                    .get(
                        `/api/user/${this.userId}/card-approval/pdf/${exportId}`,
                    )
                    .then(({ data }) => {
                        if (data.status === "ready") {
                            this.pdf.generating = false;
                            window.location.href = data.url;
                        } else if (data.status === "failed") {
                            this.failPdf(
                                "Não foi possível gerar o PDF. Tente de novo.",
                            );
                        } else {
                            this.pollPdf(exportId, startedAt);
                        }
                    })
                    .catch((error) =>
                        this.failPdf(
                            error.response?.data?.errors?.[0] ??
                                "Não foi possível gerar o PDF. Tente de novo.",
                        ),
                    );
            }, PDF_POLL_INTERVAL_MS);
        },

        failPdf(message) {
            this.stopPdfPolling();
            this.pdf.generating = false;
            this.pdf.error = message;
        },

        stopPdfPolling() {
            clearTimeout(this.pdf.timer);
            this.pdf.timer = null;
        },

        formatRate(value) {
            if (value === null || value === undefined) {
                return "—";
            }

            return (
                Number(value).toLocaleString("pt-BR", {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                }) + "%"
            );
        },

        formatInt(value) {
            return Number(value ?? 0).toLocaleString("pt-BR");
        },

        formatDate(value) {
            return value.split("-").reverse().join("/");
        },
    },
};
</script>
